import { grantCovers, normalizeSessionRole } from "../identity/access";

/** Schedule approval ladder helpers (T1 → T2 → T3 → HR → Ergani). */

export const LADDER_STATUS = {
  draft: "draft",
  pending_t2: "pending_t2",
  pending_t3: "pending_t3",
  pending_hr: "pending_hr",
  ergani_final: "ergani_final",
  post_pending_t3: "post_pending_t3",
  post_pending_hr: "post_pending_hr",
};

const ROLE_TIER = {
  employee: 0,
  crew: 0,
  chef: 0,
  store_manager: 1,
  manager: 1,
  brand_manager: 2,
  ops_manager: 3,
  admin: 3,
  hr: 3,
};

const EDIT_MIN = {
  draft: 1,
  pending_t2: 2,
  pending_t3: 3,
  pending_hr: 3,
  ergani_final: 1,
  post_pending_t3: 3,
  post_pending_hr: 3,
};

function roleKey(userOrRole) {
  if (userOrRole && typeof userOrRole === "object") {
    return normalizeSessionRole(userOrRole.role);
  }
  return normalizeSessionRole(userOrRole);
}

/** Super-admin covers T1–T3 and HR. Impersonation uses the viewed role only. */
export function isScheduleSuperAdmin(userOrRole) {
  if (userOrRole && typeof userOrRole === "object") {
    if (userOrRole.impersonated) return false;
    if (userOrRole.is_super_admin || userOrRole.can_impersonate) return true;
  }
  return false;
}

export function scheduleUserTier(userOrRole) {
  if (isScheduleSuperAdmin(userOrRole)) return 3;
  return ROLE_TIER[roleKey(userOrRole)] ?? 0;
}

/** Builder access: ops.schedule.edit grant, or legacy ladder roles. */
export function canBuildSchedule(userOrRole) {
  if (isScheduleSuperAdmin(userOrRole)) return true;
  if (userOrRole && typeof userOrRole === "object") {
    if (grantCovers(userOrRole.grants, "ops", "schedule", "edit")) return true;
  }
  return ["store_manager", "admin", "brand_manager", "ops_manager", "manager", "hr"].includes(
    roleKey(userOrRole)
  );
}

export function canEditLadderWeek(userOrRole, status) {
  const need = EDIT_MIN[String(status || "draft")] ?? 3;
  return scheduleUserTier(userOrRole) >= need;
}

/** Shown to locked lower tiers while a week is in T2 / T3 / HR review. */
export function ladderLockedContactHint(userOrRole, status, lang) {
  if (canEditLadderWeek(userOrRole, status)) return null;
  const gr = lang === "gr";
  switch (String(status || "")) {
    case "pending_t2":
      return gr
        ? "Η επεξεργασία δεν είναι διαθέσιμη — το πρόγραμμα είναι σε έλεγχο από T2. Επικοινωνήστε απευθείας με T2."
        : "Edits are not available — this week is under T2 review. Contact T2 directly.";
    case "pending_t3":
    case "post_pending_t3":
      return gr
        ? "Η επεξεργασία δεν είναι διαθέσιμη — το πρόγραμμα είναι σε έλεγχο από T3. Επικοινωνήστε απευθείας με T3."
        : "Edits are not available — this week is under T3 review. Contact T3 directly.";
    case "pending_hr":
    case "post_pending_hr":
      return gr
        ? "Η επεξεργασία δεν είναι διαθέσιμη — το πρόγραμμα εκκρεμεί για HR. Επικοινωνήστε απευθείας με HR."
        : "Edits are not available — this week is pending HR upload. Contact HR directly.";
    default:
      return gr
        ? "Η επεξεργασία δεν είναι διαθέσιμη για αυτή την κατάσταση εβδομάδας."
        : "Edits are not available for this week status.";
  }
}

export function ladderStatusLabel(status, lang) {
  const gr = lang === "gr";
  switch (String(status || "")) {
    case "draft":
      return gr ? "Πρόχειρο — T1" : "Draft — T1";
    case "pending_t2":
      return gr ? "Αναμονή έγκρισης T2 managers" : "Pending T2 managers approval";
    case "pending_t3":
      return gr ? "Αναμονή έγκρισης T3 managers" : "Pending T3 managers approval";
    case "pending_hr":
      return gr ? "Αναμονή ανεβάσματος HR" : "Pending HR upload";
    case "ergani_final":
      return gr ? "Στο Ergani (τελικό)" : "Ergani final";
    case "post_pending_t3":
      return gr ? "Επείγον — αναμονή έγκρισης T3 managers" : "Urgent — pending T3 managers approval";
    case "post_pending_hr":
      return gr ? "Επείγον — αναμονή HR" : "Urgent — pending HR upload";
    default:
      return status || "—";
  }
}

/** SoftOne uploads only after T3 has sent the week to HR (or urgent post-HR path). */
export function ladderAllowsSoftonePush(status) {
  const s = String(status || "");
  return s === LADDER_STATUS.pending_hr || s === LADDER_STATUS.post_pending_hr;
}

/** Where Reject sends the week from the current review status. */
export function ladderRejectReturnLabel(status, lang) {
  const gr = lang === "gr";
  switch (String(status || "")) {
    case "pending_t2":
      return gr ? "πρόχειρο (T1)" : "draft (T1)";
    case "pending_t3":
    case "post_pending_t3":
      return gr ? "T2 managers" : "T2 managers";
    case "pending_hr":
    case "post_pending_hr":
      return gr ? "T3 managers" : "T3 managers";
    default:
      return gr ? "προηγούμενη βαθμίδα" : "the previous tier";
  }
}

/**
 * Active reject notice for the tier that received the week back.
 * Hidden after the week is advanced again (last_notify_kind changes).
 */
export function ladderRejectNotice(week, lang) {
  if (!week) return null;
  const kind = String(week.last_notify_kind || "");
  const status = String(week.status || "");
  const relevant =
    (kind === "reject_to_t1" && status === "draft") ||
    (kind === "reject_to_t2" && status === "pending_t2") ||
    (kind === "reject_to_t3" && (status === "pending_t3" || status === "post_pending_t3"));
  if (!relevant) return null;
  if (!week.last_reject_at && !week.last_reject_comment && !week.last_reject_by_name) {
    return null;
  }
  const gr = lang === "gr";
  const by = String(week.last_reject_by_name || "").trim();
  const comment = String(week.last_reject_comment || "").trim();
  let when = "";
  if (week.last_reject_at) {
    const d = new Date(week.last_reject_at);
    if (!Number.isNaN(d.getTime())) {
      when = d.toLocaleString(gr ? "el-GR" : "en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }
  }
  const fromTier =
    kind === "reject_to_t1"
      ? "T2"
      : kind === "reject_to_t2"
        ? "T3"
        : "HR";
  const title = gr
    ? `Απορρίφθηκε από ${fromTier}${by ? ` (${by})` : ""}`
    : `Rejected by ${fromTier}${by ? ` (${by})` : ""}`;
  return {
    title,
    meta: when || "",
    comment:
      comment ||
      (gr ? "Δεν δόθηκε σχόλιο." : "No comment provided."),
    fromTier,
  };
}

export function primaryLadderAction(userOrRole, status) {
  const r = roleKey(userOrRole);
  const s = String(status || "draft");
  const tier = scheduleUserTier(userOrRole);
  if (s === "draft" && tier >= 1) return "submit_t2";
  if (s === "pending_t2" && tier >= 2) return "approve_t2";
  if (s === "pending_t3" && tier >= 3) return "approve_t3";
  if (s === "ergani_final" && tier >= 1) return "urgent_t3";
  if (s === "post_pending_t3" && tier >= 3) return "post_to_hr";
  if (s === "pending_hr" || s === "post_pending_hr") {
    // HR marks SoftOne upload; T3/super-admin re-notifies HR after edits.
    if (r === "hr") return "mark_ergani";
    if (tier >= 3 || isScheduleSuperAdmin(userOrRole)) return "renotify_hr";
  }
  return null;
}

export function canRejectLadder(userOrRole, status) {
  const tier = scheduleUserTier(userOrRole);
  const s = String(status || "");
  if (s === "pending_t2") return tier >= 2;
  if (s === "pending_t3" || s === "pending_hr") return tier >= 3;
  return false;
}

export const LADDER_ENDPOINTS = {
  submit_t2: "/schedule/weeks/submit-t2",
  approve_t2: "/schedule/weeks/approve-t2",
  approve_t3: "/schedule/weeks/approve-t3",
  renotify_hr: "/schedule/weeks/renotify-hr",
  reject: "/schedule/weeks/reject",
  urgent_t3: "/schedule/weeks/urgent-notify-t3",
  post_to_hr: "/schedule/weeks/send-post-to-hr",
  mark_ergani: "/schedule/weeks/mark-ergani",
};

/** Tiers that can edit before this send-forward must pass schedule-rule checks. */
export const LADDER_ACTIONS_REQUIRE_VALIDATION = {
  submit_t2: true,
  approve_t2: true,
  approve_t3: true,
  renotify_hr: true,
  urgent_t3: true,
  post_to_hr: true,
};

export function ladderActionRequiresValidation(action) {
  return Boolean(LADDER_ACTIONS_REQUIRE_VALIDATION[String(action || "")]);
}

/**
 * Expected week status after a successful ladder POST (before/without server body).
 * Reject depends on the status the week had when the action ran.
 */
export function ladderStatusAfterAction(action, currentStatus) {
  const a = String(action || "");
  const s = String(currentStatus || "draft");
  switch (a) {
    case "submit_t2":
      return LADDER_STATUS.pending_t2;
    case "approve_t2":
      return LADDER_STATUS.pending_t3;
    case "approve_t3":
      return LADDER_STATUS.pending_hr;
    case "renotify_hr":
      return s === LADDER_STATUS.post_pending_hr
        ? LADDER_STATUS.post_pending_hr
        : LADDER_STATUS.pending_hr;
    case "urgent_t3":
      return LADDER_STATUS.post_pending_t3;
    case "post_to_hr":
      return LADDER_STATUS.post_pending_hr;
    case "mark_ergani":
      return LADDER_STATUS.ergani_final;
    case "reject":
      if (s === LADDER_STATUS.pending_t2) return LADDER_STATUS.draft;
      if (s === LADDER_STATUS.pending_t3 || s === LADDER_STATUS.post_pending_t3) {
        return LADDER_STATUS.pending_t2;
      }
      if (s === LADDER_STATUS.pending_hr || s === LADDER_STATUS.post_pending_hr) {
        return LADDER_STATUS.pending_t3;
      }
      return s;
    default:
      return s;
  }
}

/** Copy for ladder confirm when client alerts exist — review prior-tier explanations. */
export function ladderPriorReviewHint(action, lang) {
  const gr = lang === "gr";
  switch (String(action || "")) {
    case "approve_t2":
      return gr
        ? "Υπάρχουν alerts/warnings. Ελέγξτε τις εξηγήσεις που έδωσε το T1 πριν εγκρίνετε."
        : "There are alerts/warnings. Verify the explanations T1 gave before you approve.";
    case "approve_t3":
      return gr
        ? "Υπάρχουν alerts/warnings. Ελέγξτε τις εξηγήσεις που έδωσαν T1 και T2 πριν εγκρίνετε."
        : "There are alerts/warnings. Verify the explanations T1 and T2 gave before you approve.";
    case "renotify_hr":
      return gr
        ? "Υπάρχουν alerts/warnings. Ελέγξτε τις εξηγήσεις πριν ειδοποιήσετε ξανά το HR."
        : "There are alerts/warnings. Review explanations before re-notifying HR.";
    case "submit_t2":
      return gr
        ? "Υπάρχουν alerts/warnings. Διορθώστε τα ή επιβεβαιώστε ότι τα έχετε ελέγξει πριν ειδοποιήσετε το T2."
        : "There are alerts/warnings. Fix them or confirm you have reviewed them before notifying T2.";
    default:
      return gr
        ? "Υπάρχουν alerts/warnings. Ελέγξτε τα πριν συνεχίσετε."
        : "There are alerts/warnings. Review them before you continue.";
  }
}

export function ladderActionLabel(action, lang) {
  const gr = lang === "gr";
  switch (action) {
    case "submit_t2":
      return gr ? "Ειδοποίηση T2 (brand)" : "Notify T2";
    case "approve_t2":
      return gr ? "Έγκριση → T3" : "Approve → T3";
    case "approve_t3":
      return gr ? "Έγκριση → HR" : "Approve → HR";
    case "renotify_hr":
      return gr ? "Έγκριση → HR" : "Approve → HR";
    case "urgent_t3":
      return gr ? "Επείγον → T3" : "Urgent notify T3";
    case "post_to_hr":
      return gr ? "Αποστολή σε HR" : "Send to HR";
    case "mark_ergani":
      return gr ? "Δημοσίευση στο προσωπικό" : "Publish to Crew";
    case "reject":
      return gr ? "Απόρριψη" : "Reject";
    default:
      return action || "";
  }
}
